// Shared constants: brand assets, contact info, service definitions, nav.
import {
  LifeBuoy,
  ShieldCheck,
  Server,
  Phone,
  Layers,
  KeyRound,
  RefreshCw,
  Activity,
  ClipboardList,
  Cloud,
  DatabaseBackup,
  MapPin,
  Users2,
  PhoneCall,
  Smartphone,
  Workflow,
  Settings2,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export const LOGOS = {
  main: 'https://cdn.magicpatterns.com/uploads/sZo6ofHHH3UDxg1SosgSFv/CTGroup_Logo.svg',
  managed:
    'https://cdn.magicpatterns.com/uploads/pErG9b7rVssDbLyAt51476/CTGroup_Solutions_Logo.svg',
  hosting:
    'https://cdn.magicpatterns.com/uploads/463krMVQAJwULPN7fKXvy2/CTGroup_Cloud_Logo.svg',
  security:
    'https://cdn.magicpatterns.com/uploads/8KKSjPFfWBBBikRHFzPFJE/CTGroup_Security_Logo.svg',
  voice:
    'https://cdn.magicpatterns.com/uploads/rK1aFjjYNmE3VRwqTsVogm/CTGroup_Voice_Logo.svg',
}

export const IMAGES = {
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/21580da2-36b2-4159-a5ba-037c49b6ca82.jpg',
  datacentre:
    'https://cdn.magicpatterns.com/patterns/generated-images/98f33ab5-c2a1-4c91-9ece-4cd1009f8536.jpg',
  legal:
    'https://cdn.magicpatterns.com/patterns/generated-images/61bcedbe-5d3c-44aa-bf37-cb91e98fd600.jpg',
  accounting:
    'https://cdn.magicpatterns.com/patterns/generated-images/2fd259a0-114e-4329-957e-3ed75c3b426b.jpg',
  cbd: 'https://cdn.magicpatterns.com/patterns/generated-images/e7cae718-b2bb-48ba-b71e-aa5224e675c9.jpg',
}

export const CONTACT = {
  phone: '1300 434 237',
  phoneHref: 'tel:1300434237',
  email: 'solutions@ctgroup.com.au',
  emailHref: 'mailto:solutions@ctgroup.com.au',
  growthName: 'Michael Ryan',
  growthEmail: 'michael.ryan@ctgroup.com.au',
  growthEmailHref: 'mailto:michael.ryan@ctgroup.com.au',
  ceo: 'Clarence Chuah',
}

export interface ServiceFeature {
  icon: LucideIcon
  label: string
}

export interface Service {
  id: string
  name: string
  logo: string
  accent: string // hex
  accentVar: string
  tintClass: string
  copy: string
  features: ServiceFeature[]
  cta: string
}

export const SERVICES: Service[] = [
  {
    id: 'managed',
    name: 'Managed Services',
    logo: LOGOS.managed,
    accent: '#0AA77E',
    accentVar: 'var(--svc-green-deep)',
    tintClass: 'rgba(117,185,101,0.08)',
    copy: 'End-to-end IT management, responsive user support, vendor coordination and practical technology planning.',
    features: [
      { icon: LifeBuoy, label: 'Service desk and user support' },
      { icon: Layers, label: 'Software and licensing oversight' },
      { icon: Users2, label: 'Vendor management' },
      { icon: Activity, label: 'Infrastructure monitoring' },
      { icon: ClipboardList, label: 'Technology planning' },
    ],
    cta: 'Explore Managed Services',
  },
  {
    id: 'security',
    name: 'Security',
    logo: LOGOS.security,
    accent: '#D2223D',
    accentVar: 'var(--svc-red)',
    tintClass: 'rgba(210,34,61,0.06)',
    copy: 'Layered cyber security that protects client trust, business continuity and the commercial health of your firm.',
    features: [
      { icon: ShieldCheck, label: 'Security posture reviews' },
      { icon: Activity, label: 'Managed controls and monitoring' },
      { icon: ClipboardList, label: 'Essential Eight guidance' },
      { icon: KeyRound, label: 'Identity and access protection' },
      { icon: RefreshCw, label: 'Recovery planning' },
    ],
    cta: 'Explore Security',
  },
  {
    id: 'hosting',
    name: 'Hosting',
    logo: LOGOS.hosting,
    accent: '#57578E',
    accentVar: 'var(--svc-purple-deep)',
    tintClass: 'rgba(158,117,156,0.08)',
    copy: 'Private cloud, server and data-centre infrastructure operated by CT Group for control, performance and resilience.',
    features: [
      { icon: Cloud, label: 'Private cloud hosting' },
      { icon: Server, label: 'Application hosting' },
      { icon: DatabaseBackup, label: 'Backup and business continuity' },
      { icon: MapPin, label: 'Australian infrastructure' },
      { icon: Users2, label: 'Scalable per-user environments' },
    ],
    cta: 'Explore Hosting',
  },
  {
    id: 'voice',
    name: 'Voice',
    logo: LOGOS.voice,
    accent: '#F2612A',
    accentVar: 'var(--svc-orange-deep)',
    tintClass: 'rgba(247,148,41,0.08)',
    copy: 'Reliable business telephony and Microsoft Teams calling that keep clients and teams connected wherever they work.',
    features: [
      { icon: PhoneCall, label: 'Hosted VoIP' },
      { icon: Phone, label: 'Microsoft Teams calling' },
      { icon: Smartphone, label: 'Mobile and office integration' },
      { icon: Workflow, label: 'Call flow design' },
      { icon: Settings2, label: 'Ongoing management' },
    ],
    cta: 'Explore Voice',
  },
]

export interface NavItem {
  label: string
  children: { label: string; accent?: string }[]
}

export const NAV: NavItem[] = [
  {
    label: 'Services',
    children: [
      { label: 'Managed Services', accent: '#0AA77E' },
      { label: 'Security', accent: '#D2223D' },
      { label: 'Hosting', accent: '#57578E' },
      { label: 'Voice', accent: '#F2612A' },
      { label: 'Service overview' },
    ],
  },
  {
    label: 'Industries',
    children: [
      { label: 'Legal' },
      { label: 'Accounting' },
      { label: 'Engineering' },
      { label: 'Health' },
    ],
  },
  {
    label: 'Why CT Group',
    children: [
      { label: 'Our Difference' },
      { label: 'Our Own Infrastructure' },
      { label: 'Our Story' },
      { label: 'The Independent IT Review' },
    ],
  },
  {
    label: 'Client Outcomes',
    children: [{ label: 'Case Studies' }, { label: 'Testimonials' }],
  },
  {
    label: 'Resources',
    children: [
      { label: 'Essential Eight Data Pack' },
      { label: 'Switching Providers Guide' },
      { label: 'IT Review Explainer' },
      { label: 'Insights' },
      { label: 'FAQ' },
    ],
  },
]
